/**
 * YouTube Analytics Adapter (Story 4.9, IMC-12 Expansion)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls and delegates discovery to
 * the existing discoverYouTubeChannels() function.
 *
 * TWO APIs used:
 * - YouTube Analytics API v2 (youtubeanalytics.googleapis.com) — metrics/reports
 * - YouTube Data API v3 (youtube.googleapis.com) — channel discovery, video metadata
 *
 * ADR-013: Adapter Pattern for API Isolation
 * ADR-014: Defensive API Versioning (v2/v3 via API_VERSIONS)
 *
 * IMC-12: Expanded from 9 metrics / 6 dimensions to 42 metrics / 20 dimensions.
 * IMC-24: Expanded to 48 metrics / 23 dimensions. Added annotations (4), retention (2), 3 new dimensions.
 * Multiple report types: basic, reach, cards, playlists, livestream, revenue, annotations, retention.
 *
 * OAuth is NOT part of this adapter — handled by Story 4.5.
 * Token refresh is NOT implemented — deferred to Story 4.12+.
 * KV caching is NOT implemented — deferred to Story 4.11.
 */

import type {
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  DimensionDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { discoverYouTubeChannels } from "./discovery";
import { API_VERSIONS } from "../config";

// ============================================================================
// Constants
// ============================================================================

const PLATFORM = "youtube-analytics" as const;

const YOUTUBE_ANALYTICS_BASE = "https://youtubeanalytics.googleapis.com";
const YOUTUBE_DATA_BASE = "https://youtube.googleapis.com/youtube";

/** Per-call timeout for Analytics queries.
 * Timeout budget: 11 queries in parallel via Promise.all (~2.5s wall clock) + 1 enrichment + 1 retention × 2.5s = ~7.5s total.
 * IMC-12: Reduced from 5s to 2.5s. IMC-24: +1 annotations query + 1 retention follow-up. */
const QUERY_TIMEOUT_MS = 2_500;

/** Video enrichment timeout (2s — shorter to stay under 30s Worker limit) */
const ENRICHMENT_TIMEOUT_MS = 2_000;

/** Validation timeout (5s for lightweight Data API call) */
const VALIDATE_TIMEOUT_MS = 5_000;

/** Max results for paginated queries */
const MAX_TOP_VIDEOS = 10;
const MAX_GEOGRAPHY_RESULTS = 25;
const MAX_PLAYLISTS = 10;

// ============================================================================
// Report Index Constants
// ============================================================================

const REPORT_INDEX = {
  OVERVIEW: 0,
  TOP_VIDEOS: 1,
  TRAFFIC_SOURCES: 2,
  DEMOGRAPHICS: 3,
  GEOGRAPHY: 4,
  REACH: 5,
  CARDS: 6,
  PLAYLISTS: 7,
  LIVESTREAM: 8,
  REVENUE: 9,
  ANNOTATIONS: 10,
  RETENTION: 11,
} as const;

// ============================================================================
// YouTube Analytics Response Types (local to adapter, NOT exported)
// ============================================================================

interface YouTubeAnalyticsResponse {
  kind?: string;
  columnHeaders?: Array<{
    name: string;
    dataType: string;
    columnType: string;
  }>;
  rows?: Array<Array<string | number>> | null;
}

interface VideoSnippet {
  title?: string;
  thumbnails?: {
    default?: { url?: string };
  };
}

interface VideoListItem {
  id?: string;
  snippet?: VideoSnippet;
}

interface VideoListResponse {
  items?: VideoListItem[];
}

// ============================================================================
// Traffic Source Labels
// ============================================================================

const TRAFFIC_SOURCE_LABELS: Record<string, string> = {
  YT_SEARCH: "YouTube Search",
  SUGGESTED: "Suggested Videos",
  EXT_URL: "External",
  NO_LINK_EMBEDDED: "Embedded (No Link)",
  NO_LINK_OTHER: "Other (No Link)",
  SUBSCRIBER: "Subscriber Feed",
  YT_CHANNEL: "Channel Page",
  YT_OTHER_PAGE: "Other YouTube Pages",
  NOTIFICATION: "Notifications",
  PLAYLIST: "Playlist",
  END_SCREEN: "End Screen",
  CAMPAIGN_CARD: "Campaign Card",
  ADVERTISING: "Advertising",
  SHORTS: "YouTube Shorts",
  PRODUCT_PAGE: "Product Page",
  HASHTAGS: "Hashtags",
  LIVE_REDIRECT: "Live Redirect",
  SOUND_PAGE: "Sound Page",
  RELATED_VIDEO: "Related Video",
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * Format channel ID for YouTube Analytics `ids` parameter.
 * CRITICAL: Uses double-equals (`channel==`) — single equals silently returns empty data.
 */
function formatChannelIds(channelId: string): string {
  return `channel==${channelId}`;
}

/**
 * Build standard YouTube API headers (Bearer token + Accept).
 * No developer token needed — unlike Google Ads.
 */
function buildHeaders(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: "application/json",
  };
}

/**
 * Parse YouTube Analytics tabular response format.
 * Maps columnHeaders to row values by name — NEVER by position index.
 * Returns empty array for null/missing rows (not an error).
 */
function parseAnalyticsResponse(
  data: YouTubeAnalyticsResponse,
  _queryName: string
): AdapterResult<Record<string, unknown>[]> {
  const headers = data.columnHeaders;
  const rows = data.rows ?? [];

  if (!headers || headers.length === 0) {
    return adapterOk([]);
  }

  return adapterOk(
    rows.map((row) =>
      Object.fromEntries(
        headers.map((header, i) => [header.name, row[i]])
      )
    )
  );
}

/**
 * Check if a YouTube 403 response is actually a quota exhaustion error.
 * YouTube returns 403 (NOT 429) for quota limits with specific reason strings.
 */
function isQuotaExhausted(providerMessage: string | undefined): boolean {
  if (!providerMessage) return false;
  return (
    providerMessage.includes("quotaExceeded") ||
    providerMessage.includes("rateLimitExceeded")
  );
}

// ============================================================================
// Available Metrics Definition
// ============================================================================

const YOUTUBE_ANALYTICS_METRICS: MetricDefinition[] = [
  // Views & Reach (6)
  { key: "views", label: "Views", type: "number", category: "reach", dataType: "Integer", aggregationKind: "additive" },
  { key: "engagedViews", label: "Engaged Views", type: "number", category: "reach", dataType: "Integer", aggregationKind: "additive" },
  { key: "redViews", label: "Red Views (Premium)", type: "number", category: "reach", dataType: "Integer", aggregationKind: "additive" },
  { key: "videoThumbnailImpressions", label: "Thumbnail Impressions", type: "number", category: "reach", dataType: "Integer", aggregationKind: "additive" },
  { key: "videoThumbnailImpressionsClickRate", label: "Thumbnail CTR", type: "percentage", category: "reach", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "videoThumbnailImpressions" } },
  { key: "uniques", label: "Unique Viewers", type: "number", category: "reach", dataType: "Integer", aggregationKind: "additive" },

  // Watch Time (4)
  { key: "estimatedMinutesWatched", label: "Watch Time (min)", type: "number", category: "watchTime", dataType: "Duration", aggregationKind: "additive" },
  { key: "estimatedRedMinutesWatched", label: "Red Watch Time (min)", type: "number", category: "watchTime", dataType: "Duration", aggregationKind: "additive" },
  { key: "averageViewDuration", label: "Avg View Duration", type: "duration", category: "watchTime", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "views" } },
  { key: "averageViewPercentage", label: "Avg View %", type: "percentage", category: "watchTime", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "views" } },

  // Engagement (8)
  { key: "likes", label: "Likes", type: "number", category: "engagement", dataType: "Integer", aggregationKind: "additive" },
  { key: "dislikes", label: "Dislikes", type: "number", category: "engagement", dataType: "Integer", aggregationKind: "additive" },
  { key: "comments", label: "Comments", type: "number", category: "engagement", dataType: "Integer", aggregationKind: "additive" },
  { key: "shares", label: "Shares", type: "number", category: "engagement", dataType: "Integer", aggregationKind: "additive" },
  { key: "subscribersGained", label: "Subscribers Gained", type: "number", category: "engagement", dataType: "Integer", aggregationKind: "additive" },
  { key: "subscribersLost", label: "Subscribers Lost", type: "number", category: "engagement", dataType: "Integer", aggregationKind: "additive" },
  { key: "videosAddedToPlaylists", label: "Added to Playlists", type: "number", category: "engagement", dataType: "Integer", aggregationKind: "additive" },
  { key: "videosRemovedFromPlaylists", label: "Removed from Playlists", type: "number", category: "engagement", dataType: "Integer", aggregationKind: "additive" },

  // Card & End Screen (6)
  { key: "cardImpressions", label: "Card Impressions", type: "number", category: "cards", dataType: "Integer", aggregationKind: "additive" },
  { key: "cardClicks", label: "Card Clicks", type: "number", category: "cards", dataType: "Integer", aggregationKind: "additive" },
  { key: "cardClickRate", label: "Card CTR", type: "percentage", category: "cards", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "cardClicks", denominator: "cardImpressions" } },
  { key: "cardTeaserImpressions", label: "Card Teaser Impressions", type: "number", category: "cards", dataType: "Integer", aggregationKind: "additive" },
  { key: "cardTeaserClicks", label: "Card Teaser Clicks", type: "number", category: "cards", dataType: "Integer", aggregationKind: "additive" },
  { key: "cardTeaserClickRate", label: "Card Teaser CTR", type: "percentage", category: "cards", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "cardTeaserClicks", denominator: "cardTeaserImpressions" } },

  // Livestream (2)
  { key: "averageConcurrentViewers", label: "Avg Concurrent Viewers", type: "number", category: "livestream", dataType: "Integer", aggregationKind: "ratio" },
  { key: "peakConcurrentViewers", label: "Peak Concurrent Viewers", type: "number", category: "livestream", dataType: "Integer", aggregationKind: "ratio" },

  // Annotation (4) — annotations deprecated since 2019, historical data still queryable
  { key: "annotationClickThroughRate", label: "Annotation CTR", type: "percentage", category: "annotations", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "annotationClicks", denominator: "annotationImpressions" } },
  { key: "annotationCloseRate", label: "Annotation Close Rate", type: "percentage", category: "annotations", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "annotationImpressions" } },
  { key: "annotationImpressions", label: "Annotation Impressions", type: "number", category: "annotations", dataType: "Integer", aggregationKind: "additive" },
  { key: "annotationClicks", label: "Annotation Clicks", type: "number", category: "annotations", dataType: "Integer", aggregationKind: "additive" },

  // Audience Retention (2) — channel-level aggregates
  { key: "audienceWatchRatio", label: "Audience Watch Ratio", type: "number", category: "retention", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "views" } },
  { key: "relativeRetentionPerformance", label: "Relative Retention", type: "number", category: "retention", dataType: "Decimal", aggregationKind: "ratio" },

  // Revenue (8) — requires yt-analytics-monetary.readonly scope
  { key: "estimatedRevenue", label: "Estimated Revenue", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "additive" },
  { key: "estimatedAdRevenue", label: "Estimated Ad Revenue", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "additive" },
  { key: "estimatedRedPartnerRevenue", label: "Premium Revenue", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "additive" },
  { key: "grossRevenue", label: "Gross Revenue", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "additive" },
  { key: "cpm", label: "CPM", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { denominator: "adImpressions" } },
  { key: "playbackBasedCpm", label: "Playback-Based CPM", type: "currency", category: "revenue", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { denominator: "monetizedPlaybacks" } },
  { key: "adImpressions", label: "Ad Impressions", type: "number", category: "revenue", dataType: "Integer", aggregationKind: "additive" },
  { key: "monetizedPlaybacks", label: "Monetized Playbacks", type: "number", category: "revenue", dataType: "Integer", aggregationKind: "additive" },

  // Playlist (7)
  { key: "playlistViews", label: "Playlist Views", type: "number", category: "playlist", dataType: "Integer", aggregationKind: "additive" },
  { key: "playlistEstimatedMinutesWatched", label: "Playlist Watch Time", type: "number", category: "playlist", dataType: "Duration", aggregationKind: "additive" },
  { key: "playlistStarts", label: "Playlist Starts", type: "number", category: "playlist", dataType: "Integer", aggregationKind: "additive" },
  { key: "playlistSaves", label: "Playlist Saves", type: "number", category: "playlist", dataType: "Integer", aggregationKind: "additive" },
  { key: "playlistAverageViewDuration", label: "Playlist Avg Duration", type: "duration", category: "playlist", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "playlistStarts" } },
  { key: "averageTimeInPlaylist", label: "Avg Time in Playlist", type: "duration", category: "playlist", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "playlistStarts" } },
  { key: "viewsPerPlaylistStart", label: "Views/Playlist Start", type: "number", category: "playlist", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "playlistViews", denominator: "playlistStarts" } },

  // Demographics (1) — kept for demographics report
  { key: "viewerPercentage", label: "Viewer Percentage", type: "percentage", category: "demographics", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "views" } },
];

// ============================================================================
// Query Execution
// ============================================================================

/**
 * Execute a single YouTube Analytics API query.
 * On 403, inspects response body for quota exhaustion vs real permission error.
 */
async function executeAnalyticsQuery(
  channelId: string,
  params: Record<string, string>,
  queryName: string,
  headers: Record<string, string>,
  timeoutMs: number
): Promise<AdapterResult<Record<string, unknown>[]>> {
  const queryParams = new URLSearchParams({
    ids: formatChannelIds(channelId),
    ...params,
  });

  const url = `${YOUTUBE_ANALYTICS_BASE}/${API_VERSIONS.youtubeAnalytics}/reports?${queryParams.toString()}`;

  const result = await safeFetch(url, { headers }, timeoutMs);

  if (!result.success) {
    // Check if 403 is actually quota exhaustion (YouTube uses 403, NOT 429 for quota)
    if (
      result.error.providerStatus === 403 &&
      isQuotaExhausted(result.error.providerMessage)
    ) {
      return adapterErr({
        code: "rate_limited",
        message: "Rate limit reached. Please try again later.",
        retryable: true,
        providerStatus: 403,
        providerMessage: result.error.providerMessage,
      });
    }
    return adapterErr(result.error);
  }

  try {
    const data = (await result.data.json()) as YouTubeAnalyticsResponse;
    return parseAnalyticsResponse(data, queryName);
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: sanitizeErrorMessage(
        `Failed to parse YouTube Analytics ${queryName} response: ${err instanceof Error ? err.message : String(err)}`
      ),
      retryable: false,
    });
  }
}

// ============================================================================
// Video Metadata Enrichment
// ============================================================================

/**
 * Fetch video metadata (titles, thumbnails) from YouTube Data API.
 * Matches results by video ID using a Map (NOT by array position).
 * Graceful degradation: if enrichment fails, returns empty Map.
 */
async function enrichVideoMetadata(
  videoIds: string[],
  headers: Record<string, string>
): Promise<Map<string, { title: string; thumbnailUrl: string }>> {
  const enrichmentMap = new Map<
    string,
    { title: string; thumbnailUrl: string }
  >();

  if (videoIds.length === 0) return enrichmentMap;

  // Batch all video IDs in one call (up to 50 per call, quota cost: 1 unit)
  const url = `${YOUTUBE_DATA_BASE}/${API_VERSIONS.youtube}/videos?part=snippet&id=${videoIds.join(",")}`;

  const result = await safeFetch(url, { headers }, ENRICHMENT_TIMEOUT_MS);

  if (!result.success) {
    // Enrichment failure is NOT fatal — return empty map for graceful degradation
    return enrichmentMap;
  }

  try {
    const data = (await result.data.json()) as VideoListResponse;
    for (const item of data.items ?? []) {
      if (item.id) {
        enrichmentMap.set(item.id, {
          title: item.snippet?.title ?? item.id,
          thumbnailUrl: item.snippet?.thumbnails?.default?.url ?? "",
        });
      }
    }
  } catch {
    // Parse failure — return empty map for graceful degradation
  }

  return enrichmentMap;
}

// ============================================================================
// Response Transformation
// ============================================================================

/** All overview metric keys — both original and expanded (IMC-12) */
const OVERVIEW_METRIC_KEYS = [
  "views", "estimatedMinutesWatched", "averageViewDuration",
  "subscribersGained", "subscribersLost", "likes", "dislikes",
  "comments", "shares", "videosAddedToPlaylists", "videosRemovedFromPlaylists",
  "estimatedRedMinutesWatched", "averageViewPercentage",
] as const;

function transformOverviewResults(
  records: Record<string, unknown>[]
): Record<string, unknown>[] {
  if (records.length === 0) {
    const defaults: Record<string, unknown> = { _reportType: "overview" };
    for (const key of OVERVIEW_METRIC_KEYS) defaults[key] = 0;
    return [defaults];
  }

  const row = records[0]!;
  const result: Record<string, unknown> = { _reportType: "overview" };
  for (const key of OVERVIEW_METRIC_KEYS) result[key] = row[key] ?? 0;
  return [result];
}

function transformReachResults(
  records: Record<string, unknown>[]
): Record<string, unknown>[] {
  if (records.length === 0) {
    return [{
      _reportType: "reach",
      engagedViews: 0,
      videoThumbnailImpressions: 0,
      videoThumbnailImpressionsClickRate: 0,
      redViews: 0,
      uniques: 0,
    }];
  }
  const row = records[0]!;
  return [{
    _reportType: "reach",
    engagedViews: row.engagedViews ?? 0,
    videoThumbnailImpressions: row.videoThumbnailImpressions ?? 0,
    videoThumbnailImpressionsClickRate: row.videoThumbnailImpressionsClickRate ?? 0,
    redViews: row.redViews ?? 0,
    uniques: row.uniques ?? 0,
  }];
}

function transformCardResults(
  records: Record<string, unknown>[]
): Record<string, unknown>[] {
  if (records.length === 0) {
    return [{
      _reportType: "cards",
      cardImpressions: 0, cardClicks: 0, cardClickRate: 0,
      cardTeaserImpressions: 0, cardTeaserClicks: 0, cardTeaserClickRate: 0,
    }];
  }
  const row = records[0]!;
  return [{
    _reportType: "cards",
    cardImpressions: row.cardImpressions ?? 0,
    cardClicks: row.cardClicks ?? 0,
    cardClickRate: row.cardClickRate ?? 0,
    cardTeaserImpressions: row.cardTeaserImpressions ?? 0,
    cardTeaserClicks: row.cardTeaserClicks ?? 0,
    cardTeaserClickRate: row.cardTeaserClickRate ?? 0,
  }];
}

function transformPlaylistResults(
  records: Record<string, unknown>[]
): Record<string, unknown>[] {
  return records.map((row) => ({
    _reportType: "playlists",
    playlist: String(row.playlist ?? ""),
    playlistViews: row.playlistViews ?? 0,
    playlistEstimatedMinutesWatched: row.playlistEstimatedMinutesWatched ?? 0,
    playlistStarts: row.playlistStarts ?? 0,
    playlistSaves: row.playlistSaves ?? 0,
    playlistAverageViewDuration: row.playlistAverageViewDuration ?? 0,
    averageTimeInPlaylist: row.averageTimeInPlaylist ?? 0,
    viewsPerPlaylistStart: row.viewsPerPlaylistStart ?? 0,
  }));
}

function transformLivestreamResults(
  records: Record<string, unknown>[]
): Record<string, unknown>[] {
  if (records.length === 0) {
    return [{
      _reportType: "livestream",
      averageConcurrentViewers: 0,
      peakConcurrentViewers: 0,
    }];
  }
  const row = records[0]!;
  return [{
    _reportType: "livestream",
    averageConcurrentViewers: row.averageConcurrentViewers ?? 0,
    peakConcurrentViewers: row.peakConcurrentViewers ?? 0,
  }];
}

function transformRevenueResults(
  records: Record<string, unknown>[]
): Record<string, unknown>[] {
  if (records.length === 0) {
    return [{
      _reportType: "revenue",
      estimatedRevenue: 0, estimatedAdRevenue: 0,
      estimatedRedPartnerRevenue: 0, grossRevenue: 0,
      cpm: 0, playbackBasedCpm: 0,
      adImpressions: 0, monetizedPlaybacks: 0,
    }];
  }
  const row = records[0]!;
  return [{
    _reportType: "revenue",
    estimatedRevenue: row.estimatedRevenue ?? 0,
    estimatedAdRevenue: row.estimatedAdRevenue ?? 0,
    estimatedRedPartnerRevenue: row.estimatedRedPartnerRevenue ?? 0,
    grossRevenue: row.grossRevenue ?? 0,
    cpm: row.cpm ?? 0,
    playbackBasedCpm: row.playbackBasedCpm ?? 0,
    adImpressions: row.adImpressions ?? 0,
    monetizedPlaybacks: row.monetizedPlaybacks ?? 0,
  }];
}

function transformTopVideoResults(
  records: Record<string, unknown>[],
  enrichmentMap: Map<string, { title: string; thumbnailUrl: string }>
): Record<string, unknown>[] {
  return records.map((row) => {
    const videoId = String(row.video ?? "");
    const metadata = enrichmentMap.get(videoId);
    return {
      _reportType: "topVideos",
      videoId,
      videoTitle: metadata?.title ?? videoId,
      thumbnailUrl: metadata?.thumbnailUrl ?? "",
      views: row.views ?? 0,
      estimatedMinutesWatched: row.estimatedMinutesWatched ?? 0,
      likes: row.likes ?? 0,
      comments: row.comments ?? 0,
      shares: row.shares ?? 0,
      averageViewDuration: row.averageViewDuration ?? 0,
    };
  });
}

function transformTrafficSourceResults(
  records: Record<string, unknown>[]
): Record<string, unknown>[] {
  return records.map((row) => {
    const sourceType = String(row.insightTrafficSourceType ?? "");
    return {
      _reportType: "trafficSources",
      sourceName:
        TRAFFIC_SOURCE_LABELS[sourceType] ?? sourceType,
      views: row.views ?? 0,
      estimatedMinutesWatched: row.estimatedMinutesWatched ?? 0,
    };
  });
}

function transformDemographicResults(
  records: Record<string, unknown>[]
): Record<string, unknown>[] {
  return records.map((row) => ({
    _reportType: "demographics",
    ageGroup: String(row.ageGroup ?? ""),
    gender: String(row.gender ?? ""),
    viewerPercentage: row.viewerPercentage ?? 0,
  }));
}

function transformGeographyResults(
  records: Record<string, unknown>[]
): Record<string, unknown>[] {
  return records.map((row) => ({
    _reportType: "geography",
    country: String(row.country ?? ""),
    views: row.views ?? 0,
    estimatedMinutesWatched: row.estimatedMinutesWatched ?? 0,
    averageViewDuration: row.averageViewDuration ?? 0,
  }));
}

function transformAnnotationResults(
  records: Record<string, unknown>[]
): Record<string, unknown>[] {
  if (records.length === 0) {
    return [{
      _reportType: "annotations",
      annotationClickThroughRate: 0,
      annotationCloseRate: 0,
      annotationImpressions: 0,
      annotationClicks: 0,
    }];
  }
  const row = records[0]!;
  return [{
    _reportType: "annotations",
    annotationClickThroughRate: row.annotationClickThroughRate ?? 0,
    annotationCloseRate: row.annotationCloseRate ?? 0,
    annotationImpressions: row.annotationImpressions ?? 0,
    annotationClicks: row.annotationClicks ?? 0,
  }];
}

function transformRetentionResults(
  records: Record<string, unknown>[]
): Record<string, unknown>[] {
  if (records.length === 0) {
    return [{
      _reportType: "retention",
      audienceWatchRatio: 0,
      relativeRetentionPerformance: 0,
    }];
  }
  // Retention API returns one row per elapsedVideoTimeRatio segment.
  // Exclude the first segment (ratio=0.0) as it always shows 1.0 watch ratio (start of video).
  // Aggregate remaining segments to channel-level averages.
  const dataRows = records.filter((row) => Number(row.elapsedVideoTimeRatio ?? 0) > 0);
  if (dataRows.length === 0) {
    return [{
      _reportType: "retention",
      audienceWatchRatio: records.length > 0 ? Number(records[0]!.audienceWatchRatio ?? 0) : 0,
      relativeRetentionPerformance: records.length > 0 ? Number(records[0]!.relativeRetentionPerformance ?? 0) : 0,
    }];
  }
  let totalWatchRatio = 0;
  let totalRelativeRetention = 0;
  for (const row of dataRows) {
    totalWatchRatio += Number(row.audienceWatchRatio ?? 0);
    totalRelativeRetention += Number(row.relativeRetentionPerformance ?? 0);
  }
  return [{
    _reportType: "retention",
    audienceWatchRatio: Math.round((totalWatchRatio / dataRows.length) * 10000) / 10000,
    relativeRetentionPerformance: Math.round((totalRelativeRetention / dataRows.length) * 10000) / 10000,
  }];
}

// ============================================================================
// Available Dimensions Definition (Story 5.34, IMC-24: +3 dimensions)
// ============================================================================

const YOUTUBE_DIMENSIONS: DimensionDefinition[] = [
  // Time (2)
  { key: "day", label: "Date", type: "Time", exampleValues: ["2026-03-01"] },
  { key: "month", label: "Month", type: "Time", exampleValues: ["2026-03"] },

  // Resource (4)
  { key: "video", label: "Video", type: "Content", exampleValues: ["dQw4w9WgXcQ"] },
  { key: "playlist", label: "Playlist", type: "Content", exampleValues: ["PLxxxxxxxx"] },
  { key: "channel", label: "Channel", type: "Content", exampleValues: ["UCxxxxxxxx"] },
  { key: "group", label: "Content Group", type: "Content", exampleValues: ["ABxxxxxxxxxx"] },

  // Device (2)
  { key: "deviceType", label: "Device Type", type: "Tech", exampleValues: ["DESKTOP", "MOBILE", "TABLET", "TV", "GAME_CONSOLE"] },
  { key: "operatingSystem", label: "Operating System", type: "Tech", exampleValues: ["ANDROID", "IOS", "WINDOWS", "MACINTOSH", "LINUX"] },

  // Playback Context (4)
  { key: "subscribedStatus", label: "Subscribed Status", type: "Audience", exampleValues: ["SUBSCRIBED", "UNSUBSCRIBED"] },
  { key: "liveOrOnDemand", label: "Live or On Demand", type: "Content", exampleValues: ["LIVE", "ON_DEMAND"] },
  { key: "creatorContentType", label: "Content Type", type: "Content", exampleValues: ["LIVE_STREAM", "SHORTS", "VIDEO_ON_DEMAND"] },
  { key: "youtubeProduct", label: "YouTube Product", type: "Content", exampleValues: ["CORE", "GAMING", "KIDS", "MUSIC"] },

  // Sharing (1)
  { key: "sharingService", label: "Sharing Service", type: "Traffic", exampleValues: ["FACEBOOK", "TWITTER", "WHATS_APP", "REDDIT"] },

  // Playback Location (2)
  { key: "insightPlaybackLocationType", label: "Playback Location", type: "Traffic", exampleValues: ["BROWSE", "CHANNEL", "EMBEDDED", "SEARCH", "WATCH"] },
  { key: "insightPlaybackLocationDetail", label: "Playback Location Detail", type: "Traffic", exampleValues: ["example.com", "reddit.com/r/videos"] },

  // Traffic Source (2)
  { key: "insightTrafficSourceType", label: "Traffic Source", type: "Traffic", exampleValues: ["SUBSCRIBER", "EXT_URL", "YT_SEARCH"] },
  { key: "insightTrafficSourceDetail", label: "Traffic Source Detail", type: "Traffic", exampleValues: ["search query", "https://example.com"] },

  // Demographics (2 — existing)
  { key: "ageGroup", label: "Age Group", type: "Demo", exampleValues: ["age18-24", "age25-34", "age35-44"] },
  { key: "gender", label: "Gender", type: "Demo", exampleValues: ["male", "female"] },

  // Geography (4)
  { key: "country", label: "Country", type: "Geo", exampleValues: ["US", "GB", "CA"] },
  { key: "province", label: "Province (US)", type: "Geo", exampleValues: ["US-CA", "US-NY", "US-TX"] },
  { key: "city", label: "City", type: "Geo", exampleValues: ["New York", "Los Angeles", "London"] },
  { key: "dma", label: "DMA Region", type: "Geo", exampleValues: ["501", "803", "602"] },
];

// ============================================================================
// YouTube Analytics Adapter (IntegrationAdapter implementation)
// ============================================================================

export const youtubeAnalyticsAdapter: IntegrationAdapter = {
  platform: PLATFORM,

  rateLimits: {
    requestsPerMinute: 60,
    requestsPerDay: 10_000,
  },

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const accountId = ctx.accountId;

    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message:
          "No YouTube channel selected. Please select a channel first.",
        retryable: false,
      });
    }

    const startDateStr = dateRange.start.toISOString().split("T")[0];
    const endDateStr = dateRange.end.toISOString().split("T")[0];

    if (!startDateStr || !endDateStr) {
      return adapterErr({
        code: "invalid_response",
        message: "Invalid date range: could not format dates",
        retryable: false,
      });
    }

    const headers = buildHeaders(credentials.accessToken);

    // 10 parallel YouTube Analytics queries via Promise.all — errors checked after all resolve
    // Each query costs 1 quota unit (10,000 units/day default)
    const queries: Array<{ name: string; params: Record<string, string>; optional?: boolean }> = [
      // [0] overview — expanded with engagement + watch time extras (IMC-12 Tasks 1-3)
      {
        name: "overview",
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          metrics: OVERVIEW_METRIC_KEYS.join(","),
        },
      },
      // [1] top videos
      {
        name: "topVideos",
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          metrics:
            "views,estimatedMinutesWatched,likes,comments,shares,averageViewDuration",
          dimensions: "video",
          sort: "-views",
          maxResults: String(MAX_TOP_VIDEOS),
        },
      },
      // [2] traffic sources
      {
        name: "trafficSources",
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          metrics: "views,estimatedMinutesWatched",
          dimensions: "insightTrafficSourceType",
          sort: "-views",
        },
      },
      // [3] demographics
      {
        name: "demographics",
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          metrics: "viewerPercentage",
          dimensions: "ageGroup,gender",
        },
      },
      // [4] geography
      {
        name: "geography",
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          metrics: "views,estimatedMinutesWatched,averageViewDuration",
          dimensions: "country",
          sort: "-views",
          maxResults: String(MAX_GEOGRAPHY_RESULTS),
        },
      },
      // [5] reach — IMC-12 Task 1: thumbnail impressions, engaged views, unique viewers
      {
        name: "reach",
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          metrics: "engagedViews,videoThumbnailImpressions,videoThumbnailImpressionsClickRate,redViews,uniques",
        },
      },
      // [6] cards — IMC-12 Task 4
      {
        name: "cards",
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          metrics: "cardImpressions,cardClicks,cardClickRate,cardTeaserImpressions,cardTeaserClicks,cardTeaserClickRate",
        },
      },
      // [7] playlists — IMC-12 Task 6: separate report type
      {
        name: "playlists",
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          metrics: "playlistViews,playlistEstimatedMinutesWatched,playlistStarts,playlistSaves,playlistAverageViewDuration,averageTimeInPlaylist,viewsPerPlaylistStart",
          dimensions: "playlist",
          sort: "-playlistViews",
          maxResults: String(MAX_PLAYLISTS),
        },
      },
      // [8] livestream — IMC-12 Task 7
      {
        name: "livestream",
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          metrics: "averageConcurrentViewers,peakConcurrentViewers",
          filters: "liveOrOnDemand==LIVE",
        },
      },
      // [9] revenue — IMC-12 Task 9: requires yt-analytics-monetary.readonly scope
      {
        name: "revenue",
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          metrics: "estimatedRevenue,estimatedAdRevenue,estimatedRedPartnerRevenue,grossRevenue,cpm,playbackBasedCpm,adImpressions,monetizedPlaybacks",
        },
        optional: true, // Most channels won't have monetization — 403 is expected
      },
      // [10] annotations — IMC-24 Task 1: deprecated since 2019, historical data only
      {
        name: "annotations",
        params: {
          startDate: startDateStr,
          endDate: endDateStr,
          metrics: "annotationClickThroughRate,annotationCloseRate,annotationImpressions,annotationClicks",
        },
        optional: true, // Annotations removed from YouTube in 2019 — API may return empty/error
      },
    ];

    // Execute all queries in parallel to avoid Worker timeout (ADR perf fix)
    // IMC-24: 11 parallel queries (10 original + annotations). Retention is a follow-up.
    const rawResults = await Promise.all(
      queries.map((q) =>
        executeAnalyticsQuery(accountId, q.params, q.name, headers, QUERY_TIMEOUT_MS)
      )
    );

    const queryResults: Record<string, unknown>[][] = [];
    for (let i = 0; i < rawResults.length; i++) {
      const result = rawResults[i]!;
      const q = queries[i]!;

      if (!result.success) {
        // Optional queries (e.g. revenue, annotations) gracefully degrade on any error —
        // most channels won't have monetization (403) and transient failures (500/timeout)
        // should not block the entire fetch
        if (q.optional) {
          queryResults.push([]);
          continue;
        }
        // Fail on first required query error (all-or-nothing)
        return adapterErr(result.error);
      }

      queryResults.push(result.data);
    }

    // Extract video IDs for metadata enrichment
    const topVideosRaw = queryResults[REPORT_INDEX.TOP_VIDEOS] ?? [];
    const videoIds = topVideosRaw
      .map((row) => String(row.video ?? ""))
      .filter((id) => id.length > 0);

    // IMC-24 Task 2: Retention query requires a specific video ID + elapsedVideoTimeRatio dimension.
    // Run AFTER the first batch so we can use the top video ID.
    // Runs in parallel with video enrichment to avoid serial wait.
    // Validate video ID format before using in API filter (defense-in-depth)
    const rawTopVideoId = videoIds[0] ?? "";
    const topVideoId = /^[a-zA-Z0-9_-]+$/.test(rawTopVideoId) ? rawTopVideoId : "";
    const retentionPromise = topVideoId
      ? executeAnalyticsQuery(
          accountId,
          {
            startDate: startDateStr,
            endDate: endDateStr,
            metrics: "audienceWatchRatio,relativeRetentionPerformance",
            dimensions: "elapsedVideoTimeRatio",
            filters: `video==${topVideoId}`,
          },
          "retention",
          headers,
          QUERY_TIMEOUT_MS
        )
      : Promise.resolve(adapterOk([] as Record<string, unknown>[]));

    // Enrichment + retention in parallel (both are optional, graceful degradation)
    const [enrichmentMap, retentionResult] = await Promise.all([
      enrichVideoMetadata(videoIds, headers),
      retentionPromise,
    ]);

    // Append retention results at REPORT_INDEX.RETENTION (optional — skip on error or no video)
    // Safety check: retention must be at the expected index after all parallel queries
    if (queryResults.length !== REPORT_INDEX.RETENTION) {
      return adapterErr({
        code: "invalid_response",
        message: `Internal error: retention index mismatch (expected ${REPORT_INDEX.RETENTION}, got ${queryResults.length})`,
        retryable: false,
      });
    }
    queryResults.push(retentionResult.success ? retentionResult.data : []);

    // Transform all query results via lookup map
    const transformers: Record<number, (data: Record<string, unknown>[]) => Record<string, unknown>[]> = {
      [REPORT_INDEX.OVERVIEW]: transformOverviewResults,
      [REPORT_INDEX.TOP_VIDEOS]: (data) => transformTopVideoResults(data, enrichmentMap),
      [REPORT_INDEX.TRAFFIC_SOURCES]: transformTrafficSourceResults,
      [REPORT_INDEX.DEMOGRAPHICS]: transformDemographicResults,
      [REPORT_INDEX.GEOGRAPHY]: transformGeographyResults,
      [REPORT_INDEX.REACH]: transformReachResults,
      [REPORT_INDEX.CARDS]: transformCardResults,
      [REPORT_INDEX.PLAYLISTS]: transformPlaylistResults,
      [REPORT_INDEX.LIVESTREAM]: transformLivestreamResults,
      [REPORT_INDEX.REVENUE]: transformRevenueResults,
      [REPORT_INDEX.ANNOTATIONS]: transformAnnotationResults,
      [REPORT_INDEX.RETENTION]: transformRetentionResults,
    };

    const allRecords: Record<string, unknown>[] = [];

    for (let i = 0; i < queryResults.length; i++) {
      const transform = transformers[i];
      const records = transform ? transform(queryResults[i]!) : [];
      allRecords.push(...records);
    }

    // Count queries that returned data (excludes optional queries that 403'd)
    let queriesWithData = 0;
    for (const r of queryResults) {
      if (r.length > 0) queriesWithData++;
    }

    // Structured JSON logging — console.log is the standard pattern for Cloudflare Workers
    console.log(
      JSON.stringify({
        event: "youtube_analytics_fetch_complete",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        queriesWithData,
        totalQueries: queries.length,
        recordCount: allRecords.length,
        enrichedVideos: enrichmentMap.size,
        timestamp: new Date().toISOString(),
      })
    );

    return adapterOk({
      platform: PLATFORM,
      accountId,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records: allRecords,
      fetchedAt: new Date(),
      recordCount: allRecords.length,
    });
  },

  async validateConnection(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    // Lightweight YouTube Data API call: GET /youtube/v3/channels?part=id&mine=true
    const url = `${YOUTUBE_DATA_BASE}/${API_VERSIONS.youtube}/channels?part=id&mine=true`;
    const headers = buildHeaders(credentials.accessToken);

    const result = await safeFetch(url, { headers }, VALIDATE_TIMEOUT_MS);

    if (!result.success) {
      return adapterErr(result.error);
    }

    // Verify response contains at least one channel
    try {
      const data = (await result.data.json()) as { items?: unknown[] };
      if (!data.items || data.items.length === 0) {
        return adapterErr({
          code: "account_not_found",
          message: "No YouTube channels found for this account",
          retryable: false,
        });
      }
    } catch {
      // JSON parse failed — connection is still valid if HTTP status was OK
    }

    return adapterOk(true);
  },

  async discoverAccounts(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Delegate to existing discoverYouTubeChannels() — already implemented
    return discoverYouTubeChannels(credentials.accessToken);
  },

  getAvailableMetrics(): MetricDefinition[] {
    return YOUTUBE_ANALYTICS_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return YOUTUBE_DIMENSIONS;
  },
};
